import { Search, MessageCircle, MoreVertical, Send } from "lucide-react"
import { Button } from "@/components/ui/Button"
import { Avatar } from "@/components/ui/Avatar"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui"
import { cn } from "@/utils/Functions"
import type { Conversation } from "../lib/data"

interface MessagesTabProps {
  conversations: Conversation[]
  searchQuery: string
  onSearchChange: (value: string) => void
  selectedConversation: string | null
  onSelectConversation: (id: string) => void
}

export const MessagesTab = ({
  conversations,
  searchQuery,
  onSearchChange,
  selectedConversation,
  onSelectConversation,
}: MessagesTabProps) => {
  const activeConv = conversations.find((c) => c.id === selectedConversation)

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-h-[calc(100vh-24rem)]">
      <div className="lg:col-span-1 bg-card border border-border rounded-2xl p-4 flex flex-col max-h-full">
        <div className="mb-4 shrink-0">
          <h3 className="text-foreground font-semibold mb-3">Messages</h3>
          <Input
            startIcon={<Search className="size-2 text-muted-foreground pointer-events-none" />}
            type="search"
            placeholder="Search messages..."
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSearchChange(e.target.value)}
            aria-label="Search messages"
          />
        </div>
        <div className="space-y-1 overflow-y-auto flex-1">
          {conversations.map((conv) => (
            <button
              key={conv.id}
              onClick={() => onSelectConversation(conv.id)}
              className={cn(
                "w-full p-3 rounded-xl text-left transition-colors",
                selectedConversation === conv.id ? "bg-muted" : "hover:bg-muted/50"
              )}
            >
              <div className="flex items-start gap-3">
                <Avatar
                  src={conv.avatar}
                  fallback={conv.contactName.charAt(0)}
                  className="size-10 shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-foreground text-sm font-medium truncate">{conv.contactName}</p>
                    {conv.unread > 0 && (
                      <Badge variant="error" size="sm">{conv.unread}</Badge>
                    )}
                  </div>
                  <p className="text-muted-foreground text-xs line-clamp-1">{conv.lastMessage}</p>
                  <p className="text-muted-foreground/50 text-xs mt-1">{conv.timestamp}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="lg:col-span-2 bg-card border border-border rounded-2xl flex flex-col max-h-full">
        {activeConv ? (
          <>
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Avatar
                    src={activeConv.avatar}
                    fallback={activeConv.contactName.charAt(0)}
                    className="size-10"
                  />
                  <div>
                    <p className="text-foreground font-medium">{activeConv.contactName}</p>
                    <p className="text-muted-foreground text-xs">Active now</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm">
                  <MoreVertical className="size-4" />
                </Button>
              </div>
            </div>

            <div className="flex-1 p-4 overflow-auto">
              <div className="space-y-4">
                <div className="text-center">
                  <p className="text-muted-foreground/50 text-xs">Today</p>
                </div>
                <div className="flex gap-3">
                  <Avatar
                    src={activeConv.avatar}
                    fallback={activeConv.contactName.charAt(0)}
                    className="size-8 shrink-0"
                  />
                  <div className="flex-1">
                    <div className="bg-muted rounded-2xl rounded-tl-sm p-3 max-w-md">
                      <p className="text-foreground text-sm">{activeConv.lastMessage}</p>
                    </div>
                    <p className="text-muted-foreground/50 text-xs mt-1 ml-2">2:34 PM</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-border">
              <div className="flex gap-2">
                <input
                  placeholder="Type a message..."
                  className="flex-1 h-10 px-4 rounded-lg bg-muted/50 border border-border text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-ring"
                />
                <Button size="sm">
                  <Send className="size-4" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageCircle className="size-12 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-foreground/60">Select a conversation</p>
              <p className="text-muted-foreground text-sm">Choose a message to view the thread</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
